/**
 * La tabla de entrevistas: quién, en qué estado, cuántas de las 15 preguntas
 * están cargadas y desde dónde se sigue el hilo en Close y en la ficha del
 * cliente.
 *
 * Usa `densidadPorSeveridad`: la entrevista sin agendar y la hecha sin registrar
 * respiran, la completa va compacta y en gris. El ojo cae en la deuda de datos
 * sin tener que ordenar nada.
 */
import { INTERVIEW_STATE_LABELS, type Interview } from '@ops/contracts'
import { Link } from 'react-router-dom'

import { formatFechaCorta } from '../../lib/format'
import { rutaCliente } from '../../lib/rutas'
import { useVincular } from '../../lib/vincular'
import {
  Button,
  DataTable,
  EmptyMetricState,
  ExternalLink,
  Icon,
  ProgressBar,
  ProvenanceBadge,
  StatusPill,
  type Column,
} from '../../ui'
import {
  SEVERIDAD_POR_ESTADO,
  provenanceDeEntrevista,
  severidadDe,
} from './resumen'
import { type Vinculos } from './vinculos'

export interface ListaEntrevistasProps {
  entrevistas: readonly Interview[]
  totalPreguntas: number
  vinculos: Vinculos
  /** Reloj compartido de la pantalla, para que la frescura sea consistente. */
  now: Date
  /** Abre el formulario de los cinco bloques. */
  onRegistrar: (entrevista: Interview) => void
}

/**
 * El lead en Close depende de una segunda consulta (ver `vinculos.ts`), así que
 * mientras no resolvió esta celda dice que está cargando en vez de afirmar que
 * la cuenta no está vinculada.
 */
function CeldaClose({
  entrevista,
  vinculos,
}: {
  entrevista: Interview
  vinculos: Vinculos
}) {
  const vincular = useVincular()

  if (vinculos.estado === 'cargando') {
    return (
      <span className="text-micro text-muted-strong">Cargando vínculo…</span>
    )
  }

  if (vinculos.estado === 'error') {
    return (
      <span className="inline-flex items-center gap-1 text-micro text-warning-text">
        <Icon name="atencion" size={10} />
        No pudimos leer el vínculo
        <span className="sr-only">
          {' '}
          {vinculos.mensajeError ?? ''}
        </span>
      </span>
    )
  }

  return (
    <ExternalLink
      link={vinculos.closeDe(entrevista.clientId)}
      variante="chip"
      sistemaFaltante="Close"
      onVincular={() =>
        vincular(`el lead en Close de ${entrevista.clientNombre}`)
      }
    >
      {entrevista.clientNombre}
    </ExternalLink>
  )
}

export function ListaEntrevistas({
  entrevistas,
  totalPreguntas,
  vinculos,
  now,
  onRegistrar,
}: ListaEntrevistasProps) {
  const columns: ReadonlyArray<Column<Interview>> = [
    {
      key: 'cliente',
      header: 'Cliente',
      rowHeader: true,
      width: '200px',
      sortValue: (fila) => fila.clientNombre,
      filterValue: (fila) => fila.clientNombre,
      render: (fila) => (
        <Link
          to={rutaCliente(fila.clientId)}
          className="font-semibold text-accent-text underline decoration-from-font underline-offset-2 hover:text-text"
        >
          {fila.clientNombre}
          <span className="sr-only"> — abrir su ficha en Clientes</span>
        </Link>
      ),
    },
    {
      key: 'estado',
      header: 'Estado',
      width: '120px',
      sortValue: (fila) => INTERVIEW_STATE_LABELS[fila.estado],
      filterValue: (fila) => INTERVIEW_STATE_LABELS[fila.estado],
      render: (fila) => (
        <StatusPill severidad={SEVERIDAD_POR_ESTADO[fila.estado]} size="sm">
          {INTERVIEW_STATE_LABELS[fila.estado]}
        </StatusPill>
      ),
    },
    {
      key: 'fecha',
      header: 'Fecha',
      width: '92px',
      sortValue: (fila) => fila.fecha,
      render: (fila) =>
        fila.fecha === null ? (
          <span className="text-micro text-muted-strong">Sin agendar</span>
        ) : (
          <span className="tabular">{formatFechaCorta(fila.fecha)}</span>
        ),
    },
    {
      key: 'entrevistador',
      header: 'Entrevistador',
      width: '150px',
      sortValue: (fila) => fila.entrevistador?.nombre ?? null,
      filterValue: (fila) => fila.entrevistador?.nombre ?? '',
      render: (fila) =>
        fila.entrevistador === null ? (
          <span className="inline-flex items-center gap-1 text-micro text-muted-strong">
            <Icon name="persona" size={11} />
            Sin asignar
          </span>
        ) : (
          <span>{fila.entrevistador.nombre}</span>
        ),
    },
    {
      key: 'respuestas',
      header: `Respuestas (de ${totalPreguntas})`,
      width: '210px',
      sortValue: (fila) => fila.respondidas,
      render: (fila) =>
        // Cero respuestas no se dibuja como una barra en cero: eso se lee como
        // "el cuestionario dio cero" en vez de "nadie lo cargó".
        fila.respondidas === 0 ? (
          <EmptyMetricState
            size="sm"
            metricNombre={`Respuestas de ${fila.clientNombre}`}
            textoAccion="Cargalo acá"
            onLoadData={() => onRegistrar(fila)}
          />
        ) : (
          <div className="flex min-w-0 flex-col gap-1">
            <ProgressBar
              pct={(fila.respondidas / totalPreguntas) * 100}
              label={`Respuestas cargadas de ${fila.clientNombre}`}
              severidad={severidadDe(fila, totalPreguntas)}
              etiqueta={`${fila.respondidas}/${totalPreguntas}`}
              size="sm"
            />
            <ProvenanceBadge
              provenance={provenanceDeEntrevista(fila, now)}
              now={now}
              variant="inline"
            />
          </div>
        ),
    },
    {
      key: 'close',
      header: 'Lead en Close',
      width: '190px',
      render: (fila) => <CeldaClose entrevista={fila} vinculos={vinculos} />,
    },
    {
      key: 'acciones',
      header: 'Cuestionario',
      width: '170px',
      render: (fila) => (
        <Button
          variante="chico"
          icono="lapiz"
          onClick={() => onRegistrar(fila)}
        >
          {fila.respondidas === 0 ? 'Registrar' : 'Ver y editar'}
          <span className="sr-only"> las respuestas de {fila.clientNombre}</span>
        </Button>
      ),
    },
  ]

  return (
    <DataTable
      columns={columns}
      rows={entrevistas}
      rowKey={(fila) => fila.id}
      rowSeverity={(fila) => severidadDe(fila, totalPreguntas)}
      densidadPorSeveridad
      filtroPlaceholder="Cliente, estado o entrevistador"
      emptyMessage="Ninguna entrevista coincide con el filtro"
      caption={
        <>
          {entrevistas.length} entrevistas del cuestionario de {totalPreguntas}{' '}
          preguntas. Carga manual: las respuestas las escribe quien entrevista.
        </>
      }
    />
  )
}
